import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { getOrdinalPostfix } from "@src/appV2/lib/utils/formatNumbers";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  CbhIcon,
  Pill,
  ScrollingNumberCounter,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useEffect, useState } from "react";

const SCORE_ANIMATION_DURATION_MS = 1500;

interface ShiftCancelledWithClipboardScoreBottomSheetProps {
  modalState: UseModalState;
  currentScore: number;
  scoreChange: number;
  isTriggeredFromNudge?: boolean;
  ranking?: {
    rank?: number;
    totalWorkers?: number;
    workerType?: string;
  };
  onContinue: () => void;
  isLimitedAccessWorker?: boolean;
}

export function ShiftCancelledWithClipboardScoreBottomSheet(
  props: ShiftCancelledWithClipboardScoreBottomSheetProps
) {
  const {
    modalState,
    currentScore,
    scoreChange,
    isTriggeredFromNudge,
    ranking,
    onContinue,
    isLimitedAccessWorker = false,
  } = props;

  const [showContinueButton, setShowContinueButton] = useState(false);
  const [startAnimation, setStartAnimation] = useState(false);

  const startScore = currentScore - scoreChange;
  const endScore = currentScore;

  const isRestricted = currentScore <= 0;

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setShowContinueButton(false);
      setStartAnimation(false);

      // Start animation after 1 second delay
      const animationTimer = setTimeout(() => {
        setStartAnimation(true);
      }, 1000);

      // Show continue button after animation completes (1s delay + 1.5s animation = 2.5s total)
      const continueButtonTimer = setTimeout(() => {
        setShowContinueButton(true);
      }, 1000 + SCORE_ANIMATION_DURATION_MS);

      return () => {
        clearTimeout(animationTimer);
        clearTimeout(continueButtonTimer);
      };
    }

    return undefined;
  }, [modalState.modalIsOpen]);

  const getBackgroundGradient = (): string => {
    // Amber gradient if new score >= 100
    if (currentScore >= 100) {
      // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
      return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(255, 236, 219, 1) 100%)";
    }

    // Default red gradient
    // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
    return "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(247, 208, 208, 1) 100%)";
  };

  const handleContinueClick = (): void => {
    onContinue();
  };

  const getSubtitle = (): string => {
    if (scoreChange === 0) {
      return "Your score didn't change for cancelling this shift.";
    }

    if (isTriggeredFromNudge) {
      return isLimitedAccessWorker
        ? "Thanks for the heads up. Doing so early has prevented you from losing Limited Access."
        : "Thanks for the heads up. Doing so early has prevented your account from being Restricted.";
    }

    return "Showing up builds trust with workplaces and ensures your team isn't left short.";
  };

  return (
    <FullScreenDialog
      modalState={modalState}
      variant="tertiary"
      PaperProps={{
        sx: {
          background: getBackgroundGradient(),
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={9}
          sx={{
            paddingTop: 10,
            paddingX: 5,
            paddingY: 8,
            textAlign: "center",
          }}
        >
          <Stack spacing={6} alignItems="center">
            <Text
              variant="h3"
              textAlign="center"
              sx={{
                paddingX: 6,
              }}
            >
              This shift has been cancelled
            </Text>
            <Stack spacing={6} alignItems="center">
              <Text
                variant="body2"
                textAlign="center"
                sx={{
                  paddingX: 6,
                  color: (theme) => theme.palette.grey[600],
                }}
              >
                Your Clipboard Score
              </Text>
              <Box
                sx={{
                  transform: "scale(2.2)",
                  transformOrigin: "center",
                  paddingY: 8,
                }}
              >
                <ScrollingNumberCounter
                  startValue={startScore}
                  endValue={startAnimation ? endScore : startScore}
                  duration={SCORE_ANIMATION_DURATION_MS}
                  variant="h1"
                  color="rgba(0, 67, 69, 1)"
                />
              </Box>
            </Stack>
            <Text
              variant="body1"
              textAlign="center"
              sx={{
                paddingX: 6,
                color: (theme) => theme.palette.text?.secondary,
              }}
            >
              {getSubtitle()}
            </Text>
          </Stack>

          <Stack spacing={5} alignItems="center">
            {isRestricted ? (
              <Pill
                label={
                  <Stack direction="row" gap={3} alignItems="center">
                    <CbhIcon type="diamond-colored" size="medium" />
                    <Text
                      semibold
                      variant="body2"
                      color={(theme) => theme.palette.error?.main ?? ""}
                    >
                      {isLimitedAccessWorker ? "Limited access removed" : "Restricted"}
                    </Text>
                  </Stack>
                }
                sx={{
                  backgroundColor: (theme) => theme.palette.background?.tertiary ?? "",
                }}
              />
            ) : (
              isDefined(ranking) &&
              isDefined(ranking.rank) &&
              isDefined(ranking.totalWorkers) &&
              isDefined(ranking.workerType) && (
                <Pill
                  label={
                    <Stack direction="row" gap={3} alignItems="center">
                      <CbhIcon type="diamond-colored" size="medium" />
                      <Text semibold variant="body2">
                        {ranking.rank}
                        {getOrdinalPostfix(ranking.rank)} of {ranking.totalWorkers} local{" "}
                        {ranking.workerType}s
                      </Text>
                    </Stack>
                  }
                  sx={{
                    backgroundColor: (theme) => theme.palette.background?.tertiary ?? "",
                    color: (theme) => theme.palette.text?.primary ?? "",
                  }}
                />
              )
            )}
          </Stack>
        </Stack>
      </Box>
      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          data-testid="shift-cancelled-continue-button"
          sx={{ visibility: showContinueButton ? "visible" : "hidden" }}
          onClick={handleContinueClick}
        >
          Continue
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
